// Marks, summary line and popovers of a check result (ADR-0085,
// DEV_TECH_DESIGN-0085 §5.3, D-12, D-13). The answer's text is never altered:
// marks are inserted beside it, built with createElement, setAttribute and
// textContent only, after DOMPurify has run — so a claim or a wording holding
// markup stays text (FR-54).
import { linkBibleReferences, parseReference, referenceKey } from './bible_links.js';
import { markOf, summaryOf } from './answer_check_wording.js';

const QUOTE_KINDS = new Set(['bible_quote', 'foreign_quote']);
// The closing marks of citation_claims._CLOSERS.
const CLOSING_MARKS = '“”"«';
const SKIPPED = '.answer-check-mark, code, pre';

function itemsOf(checks) {
    return (checks && Array.isArray(checks.items)) ? checks.items : [];
}

function buildMark(item, mark) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `answer-check-mark answer-check-mark--${item.state}`;
    button.setAttribute('aria-label', mark.title);
    button.setAttribute('data-check-title', mark.title);
    if (mark.wording) button.setAttribute('data-check-wording', mark.wording);
    button.textContent = mark.symbol;
    return button;
}

// Every occurrence of a reference that does not resolve: its text, then ❔,
// where the link would have stood (FR-49, FR-50).
function unresolvedReplacer(items) {
    const unresolved = new Map();
    items.filter(i => i.kind === 'bible_reference' && i.state === 'not_verifiable').forEach(item => {
        const parsed = parseReference(item.claim);
        if (parsed) unresolved.set(referenceKey(parsed), item);
    });
    return (reference, text) => {
        const item = unresolved.get(referenceKey(reference));
        if (!item) return null;
        const fragment = document.createDocumentFragment();
        fragment.appendChild(document.createTextNode(text));
        fragment.appendChild(buildMark(item, markOf(item)));
        return fragment;
    };
}

// The rendered text outside marks, code and pre, with where each node starts.
function textIndex(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let text = '';
    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement && node.parentElement.closest(SKIPPED)) continue;
        nodes.push({ node, start: text.length });
        text += node.nodeValue;
    }
    return { text, nodes };
}

// A claim is Markdown as written; the rendered text has no emphasis marks,
// backticks or link targets.
function searchable(claim) {
    return claim.replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/[*_`]/g, '');
}

// Where each quotation's mark goes: after the claim and its closing mark,
// searched in item order from a cursor (FR-50). Not found: no mark.
function quotationPositions(text, items) {
    const positions = [];
    let cursor = 0;
    items.filter(item => QUOTE_KINDS.has(item.kind)).forEach(item => {
        const needle = searchable(item.claim);
        const at = needle ? text.indexOf(needle, cursor) : -1;
        if (at < 0) return;
        let end = at + needle.length;
        if (CLOSING_MARKS.includes(text[end])) end += 1;
        positions.push({ end, item });
        cursor = end;
    });
    return positions;
}

// After the text node holding offset `end`; a node inside a link puts the
// mark after the link, so no button stands inside an anchor.
function insertMark(nodes, end, mark) {
    const { node, start } = nodes.filter(entry => entry.start < end).pop();
    const offset = end - start;
    const link = node.parentElement && node.parentElement.closest('a');
    if (link) {
        link.after(mark);
    } else if (offset < node.nodeValue.length) {
        node.splitText(offset).before(mark);
    } else {
        node.after(mark);
    }
}

// The mark with the character before it and the punctuation after it in one
// unbreakable span: WebKit breaks after any button, which left a lone „.“ at
// the start of the next line.
function glue(mark) {
    const anchor = document.createElement('span');
    anchor.className = 'answer-check-anchor';
    mark.before(anchor);
    const previous = anchor.previousSibling;
    if (previous && previous.nodeType === Node.TEXT_NODE && /\S$/.test(previous.nodeValue)) {
        anchor.appendChild(previous.splitText(previous.nodeValue.length - 1));
    }
    anchor.appendChild(mark);
    const next = anchor.nextSibling;
    const punctuation = next && next.nodeType === Node.TEXT_NODE
        ? next.nodeValue.match(/^[.,;:!?)\]]+/) : null;
    if (punctuation) {
        next.splitText(punctuation[0].length);
        anchor.appendChild(next);
    }
}

function markQuotations(html, items) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const { text, nodes } = textIndex(template.content);
    // Last to first, so the offsets of the earlier ones stay valid.
    quotationPositions(text, items).reverse().forEach(({ end, item }) => {
        insertMark(nodes, end, buildMark(item, markOf(item)));
    });
    template.content.querySelectorAll('.answer-check-mark').forEach(glue);
    return template.innerHTML;
}

/**
 * Sanitised answer HTML in, the same HTML with Bible links and marks out.
 * Without a check result it links exactly as before.
 */
export function renderCheckedHtml(html, checks) {
    const items = itemsOf(checks);
    if (!items.length) return linkBibleReferences(html);
    const linked = linkBibleReferences(html, { replaceRef: unresolvedReplacer(items) });
    return markQuotations(linked, items);
}

/** The summary line below the answer (FR-52), or '' without one. */
export function summaryElementHtml(checks) {
    const summary = summaryOf(itemsOf(checks));
    if (!summary) return '';
    const line = document.createElement('div');
    line.className = 'answer-check-summary mt-2 text-muted small';
    line.textContent = summary;
    return line.outerHTML;
}

// --- Popovers (D-13) ---
//
// Created on demand by delegation: chat.js appends with `innerHTML +=`, which
// re-parses the container and destroys every instance bound to a child.

let open = null; // { mark, popover }

function popoverContent(mark) {
    const content = document.createElement('div');
    const title = document.createElement('div');
    title.className = 'answer-check-popover__title';
    title.textContent = mark.getAttribute('data-check-title');
    content.appendChild(title);
    const wording = mark.getAttribute('data-check-wording');
    if (wording) {
        const quote = document.createElement('div');
        quote.className = 'answer-check-popover__wording mt-1';
        quote.textContent = wording;
        content.appendChild(quote);
    }
    return content;
}

function popoverOf(mark) {
    return globalThis.bootstrap.Popover.getOrCreateInstance(mark, {
        trigger: 'manual',
        html: true,
        placement: 'auto',
        content: popoverContent(mark)
    });
}

function hideOpen() {
    if (!open) return;
    open.popover.hide();
    open = null;
}

function toggle(mark) {
    const wasOpen = open !== null && open.mark === mark;
    hideOpen();
    if (wasOpen) return;
    const popover = popoverOf(mark);
    popover.show();
    open = { mark, popover };
}

function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const mark = target.closest('.answer-check-mark');
    if (mark) {
        toggle(mark);
    } else if (!target.closest('.popover')) {
        hideOpen();
    }
}

function onKeydown(event) {
    if (event.key === 'Escape') hideOpen();
}

/** One click and one keydown listener on the document (FR-51); call once. */
export function initAnswerCheckPopovers() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
}
